import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ALCANCE_LABELS, AlcanceComunicacion, APP_DESTINO_LABELS, AppDestino, Comunicacion } from 'src/app/models/Comunicacion';
import { Usuario } from 'src/app/models/Usuario';
import { ComunicacionService } from 'src/app/services/comunicacion.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { normalizeText } from 'src/app/shared/text.util';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { API_URI } from 'src/environments/environment';

@Component({
    selector: 'app-comunicacion',
    templateUrl: './comunicacion.component.html',
    styleUrls: ['./comunicacion.component.css'],
    standalone: false
})
export class ComunicacionComponent {
  private _router = inject(Router);
  private _aRoute = inject(ActivatedRoute);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);

  private _comunicacionService = inject(ComunicacionService);
  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);

  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  // id (0 => nueva comunicación)
  id = Number(this._aRoute.snapshot.paramMap.get('id') ?? 0);
  esNueva = !this.id || this.id === 0;

  readonly alcances: { id: AlcanceComunicacion; label: string }[] = [
    { id: 'todos', label: ALCANCE_LABELS['todos'] },
    { id: 'prestadores', label: ALCANCE_LABELS['prestadores'] },
    { id: 'comercios', label: ALCANCE_LABELS['comercios'] },
    { id: 'clientes', label: ALCANCE_LABELS['clientes'] },
    { id: 'socios', label: ALCANCE_LABELS['socios'] },
    { id: 'usuario', label: ALCANCE_LABELS['usuario'] },
  ];

  readonly appsDestino: { id: AppDestino; label: string }[] = [
    { id: 'ambas', label: APP_DESTINO_LABELS['ambas'] },
    { id: 'libresalud', label: APP_DESTINO_LABELS['libresalud'] },
    { id: 'cardio', label: APP_DESTINO_LABELS['cardio'] },
  ];

  // detalle (solo lectura)
  comunicacion = signal<Comunicacion | null>(null);

  // form (alta)
  comunicacionForm = this._fb.group({
    titulo: ['', Validators.required],
    descripcion: ['', Validators.required],
    alcance: ['' as AlcanceComunicacion | '', Validators.required],
    appDestino: ['ambas' as AppDestino, Validators.required],
    enviaEmail: [true],
  });

  // imagen
  x_img?: File;
  preview_img = signal<string>('');

  // buscador de usuario destino (alcance = 'usuario')
  id_usuario_destino = signal<string>('');
  usuarioDestinoSearch = signal<string>('');
  showUsuariosList = signal<boolean>(false);
  private usuariosActivos = signal<Usuario[]>([]);
  usuariosFiltrados = computed<Usuario[]>(() => {
    const term = normalizeText(this.usuarioDestinoSearch());
    if (!term) return [];

    return this.usuariosActivos()
      .filter(u =>
        normalizeText(u.x_email).includes(term) ||
        normalizeText(u.x_nro_documento ?? '').includes(term)
      )
      .slice(0, 20);
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
        return;
      }

      if (!this.esNueva) {
        try {
          const res = await firstValueFrom(this._comunicacionService.getComunicacion(this.id));

          if (!res) {
            this.error.set('La comunicación no existe.');
            this._router.navigate(['/more-comunicaciones']);
            return;
          }

          this.comunicacion.set(res);
        } catch (e) {
          console.error(e);
          this.error.set('Error al obtener los datos de la Comunicación');
        }
      }
    });
  }

  back() {
    this._location.back();
  }

  getAlcanceLabel(alcance?: string): string {
    return ALCANCE_LABELS[alcance ?? ''] ?? (alcance ?? '');
  }

  getAppDestinoLabel(app?: string): string {
    return APP_DESTINO_LABELS[app ?? ''] ?? (app ?? '');
  }

  getImgUrl(x_img?: string | null): string {
    return x_img ? `${API_URI}uploads/images/${x_img}` : '';
  }

  // ----------------- alcance / usuario destino -----------------
  async onAlcanceChange(): Promise<void> {
    const alcance = this.comunicacionForm.controls.alcance.value;

    this.id_usuario_destino.set('');
    this.usuarioDestinoSearch.set('');
    this.showUsuariosList.set(false);

    if (alcance === 'usuario' && this.usuariosActivos().length === 0) {
      try {
        const res = await firstValueFrom(this._usuarioService.getUsuarios(true));
        this.usuariosActivos.set(res ?? []);
      } catch (e) {
        console.error(e);
        this.error.set('Error al obtener los usuarios');
      }
    }
  }

  onUsuarioDestinoInput(value: string): void {
    this.usuarioDestinoSearch.set(value);
    this.showUsuariosList.set(true);
    this.id_usuario_destino.set('');
  }

  selectUsuarioDestino(usuario: Usuario): void {
    this.id_usuario_destino.set(usuario.id ?? '');
    this.usuarioDestinoSearch.set(usuario.x_email ?? '');
    this.showUsuariosList.set(false);
  }

  // ----------------- imagen -----------------
  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      resultType: CameraResultType.Uri,
      quality: 85,
      allowEditing: false,
      correctOrientation: true,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  async capturarImagen(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('comunicacion.jpg');
      this.x_img = file;
      this.preview_img.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar imagen de comunicación:', e);
    }
  }

  quitarImagen(): void {
    this.x_img = undefined;
    this.preview_img.set('');
  }

  // ----------------- enviar -----------------
  async enviarComunicacion(): Promise<void> {
    this.error.set('');

    const v = this.comunicacionForm.value;
    const titulo = String(v.titulo ?? '').trim();
    const descripcion = String(v.descripcion ?? '').trim();
    const alcance = v.alcance as AlcanceComunicacion | '';
    const appDestino = (v.appDestino || 'ambas') as AppDestino;

    if (!titulo) { this.error.set('Debe completar el Título'); return; }
    if (!descripcion) { this.error.set('Debe completar la Descripción'); return; }
    if (!alcance) { this.error.set('Debe seleccionar el Alcance'); return; }
    if (alcance === 'usuario' && !this.id_usuario_destino()) {
      this.error.set('Debe seleccionar el Usuario destino');
      return;
    }

    await this._loadingService.run(async () => {
      const formData = new FormData();
      formData.append('x_titulo', titulo);
      formData.append('x_descripcion', descripcion);
      formData.append('x_alcance', alcance);
      formData.append('x_app', appDestino);
      formData.append('b_envia_email', v.enviaEmail ? 'true' : 'false');

      if (alcance === 'usuario') {
        formData.append('id_usuario_destino', this.id_usuario_destino());
      }
      if (this.x_img) {
        formData.append('x_img', this.x_img);
      }

      try {
        const res: any = await firstValueFrom(this._comunicacionService.enviarComunicacion(formData));
        await this._router.navigate(['/more-comunicacion', res.id]);
      } catch (e) {
        console.error(e);
        this.error.set('Error al enviar la Comunicación');
      }
    });
  }
}
